"use client";

import { FC, ReactNode, useId } from "react";
import { getDataAutomationId, getDataLinkAttributes } from "@/helpers";
import cn from "classnames";
import { CaretDownLine } from "@/icons";

type AccordionProps = {
  columnsCount: number;
  multipleOpen: boolean;
  preopenedSectionIndex: number;
  items: {
    label?: ReactNode[];
    dlt: string;
    dld: string;
    children: ReactNode[];
  }[];
};

const Accordion: FC<AccordionProps> = ({
  columnsCount,
  multipleOpen,
  preopenedSectionIndex,
  items,
}) => {
  const name = useId();

  // Distribute items into near-even columns (first columns get the extra items)
  const effectiveColumnsCount = Math.max(1, columnsCount);
  const baseSize = Math.floor(items.length / effectiveColumnsCount);
  const remainder = items.length % effectiveColumnsCount;

  let offset = 0;
  const columns = Array.from({ length: effectiveColumnsCount }, (_, i) => {
    const size = baseSize + (i < remainder ? 1 : 0);
    const col = items.slice(offset, offset + size);
    offset += size;
    return col;
  });

  const fullColumns = items.length % columnsCount || columnsCount;
  const fullColumnItemsCount = Math.ceil(items.length / columnsCount);

  return (
    <div
      className="flex w-full flex-col gap-x-8 md:flex-row"
      style={
        {
          "interpolate-size": "allow-keywords",
        } as never
      }
      {...getDataAutomationId("accordion")}
    >
      {columns.map((columnItems, columnIndex) => (
        <div
          key={`${columnItems[0]?.label}-${columnItems[0].dld}-${columnItems[0].dlt}`}
          className={cn(
            "flex w-full flex-col gap-y-4 pb-4 md:pb-0",
            columnsCount > 1 && `md:md:w-1/${columnsCount}`,
          )}
        >
          {columnItems.map((item, index) => {
            const originalIndex =
              columnIndex < fullColumns
                ? fullColumnItemsCount * columnIndex + index
                : fullColumnItemsCount * fullColumns +
                  (fullColumnItemsCount - 1) * (columnIndex - fullColumns) +
                  index;
            return (
              <details
                key={`${item.dld}-${item.dlt}`}
                className="group h-min border-b border-orange-400 transition-[height] details-content:relative details-content:h-0 details-content:overflow-hidden details-content:transition-discrete details-content:duration-500 open:details-content:h-auto hover:cursor-pointer"
                name={multipleOpen ? undefined : name}
                open={
                  preopenedSectionIndex > 0 &&
                  originalIndex === preopenedSectionIndex - 1
                }
                {...getDataAutomationId(`accordion-item-${originalIndex}`)}
              >
                <summary
                  className="group focus-visible:ring-swdc-blue-400 flex w-full items-center justify-between gap-x-6 px-2 py-5 focus:outline-none focus-visible:ring"
                  {...getDataLinkAttributes({ dlt: item.dlt, dld: item.dld })}
                >
                  {item.label}
                  <CaretDownLine
                    height={18}
                    fill="currentColor"
                    className="w-full max-w-6 transition-transform group-open:rotate-180"
                    aria-hidden
                  />
                </summary>
                <div className="cursor-default px-2 pt-2 pb-6">
                  {item.children}
                </div>
              </details>
            );
          })}
        </div>
      ))}
    </div>
  );
};

export default Accordion;
